import * as Api from '@ViewModels';
import {
	AIPromptBox,
	AIPromptBoxCredit,
	AIPromptBoxTextarea,
	AIPromptBoxToolbar,
	AIPromptBoxToolbarEnd,
} from '@HoudiniUI/ai/AIPromptBox';
import { FieldError } from '@HoudiniUI/Field';
import { InputGroupButton } from '@HoudiniUI/InputGroup';
import { Pill } from '@HoudiniUI/Pill';
import { PlusIcon2 } from '@WebComponents/svgs/icons/PlusIcon';
import { StyleDeclarationValue } from 'aphrodite';
import { useState } from 'react';
import { AIPromptUsage } from '../../../../models/AIPromptTemplate';
import { AIPromptPanel } from './AIPromptPanel';
import { HelpMeWriteMenu } from './HelpMeWriteMenu';
import { SavePromptDialog } from './SavePromptDialog';
import { useAIPromptField } from './useAIPromptField';

export { useAIPromptField };
export type { IAppliedAIPrompt } from './useAIPromptField';

export interface IAIPromptFieldCopy {
	attributionNote: string;
	menuLabel: string;
	panelDescription: string;
	/** The panel's description once the field holds text, which using a prompt would replace. */
	panelReplaceDescription: string;
	saveDescription: string;
	saveNamePlaceholder: string;
	saveNote?: string;
	savedEmptyText: string;
}

const DEFAULT_COPY: IAIPromptFieldCopy = {
	attributionNote: 'Editing the text drops this.',
	menuLabel: 'Help me write this',
	panelDescription: 'Using one fills the field.',
	panelReplaceDescription: 'Using one replaces what you have written.',
	saveDescription: 'Keep this so you can start from it next time.',
	saveNamePlaceholder: 'A name you will recognize',
	savedEmptyText: 'Nobody here has saved a prompt yet. Write one and use Save as a prompt beside it.',
};

interface IProps {
	'aria-labelledby'?: string;
	copy?: Partial<IAIPromptFieldCopy>;
	disabled?: boolean;
	field: ReturnType<typeof useAIPromptField>;
	footer?: React.ReactNode;
	/** The textarea's id, for the caller's label. */
	id: string;
	impersonationContext?: Api.IImpersonationContext;
	/** The caller's own rule, such as a length limit; placeholders are checked here. */
	invalid?: boolean;
	maxLength?: number;
	onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
	onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
	placeholder?: string;
	rows?: number;
	/** Off for a setting saved as a draft, where a [placeholder] left in is the user's to finish later. */
	showPlaceholderError?: boolean;
	showSamples?: boolean;
	textAreaStyles?: StyleDeclarationValue[];
	usage: AIPromptUsage;
}

export const AIPromptField: React.FC<IProps> = ({
	'aria-labelledby': ariaLabelledBy,
	copy: copyOverrides,
	disabled,
	field,
	footer,
	id,
	impersonationContext,
	invalid,
	maxLength,
	onBlur,
	onFocus,
	placeholder,
	rows,
	showPlaceholderError = true,
	showSamples,
	textAreaStyles = [],
	usage,
}) => {
	const copy = { ...DEFAULT_COPY, ...copyOverrides };
	const [isSavingPrompt, setIsSavingPrompt] = useState(false);
	const [isPanelOpen, setIsPanelOpen] = useState(false);

	const hasValue = field.value.trim().length > 0;
	const problem = showPlaceholderError ? field.placeholderProblem : null;
	const problemId = `${id}-problem`;

	return (
		<>
			<AIPromptBox>
				<AIPromptBoxToolbar>
					<HelpMeWriteMenu
						disabled={disabled}
						finalFocus={field.ref}
						impersonationContext={impersonationContext}
						label={copy.menuLabel}
						onBrowseAll={() => setIsPanelOpen(true)}
						onPromptSelected={field.applyPrompt}
						showSamples={showSamples}
						usage={usage}
					/>
					{/* Only once the text is the user's own: an unedited prompt is already saved. */}
					{hasValue && !field.sourcePrompt && !disabled ? (
						<AIPromptBoxToolbarEnd>
							<InputGroupButton onClick={() => setIsSavingPrompt(true)} size='sm'>
								<PlusIcon2 height={14} width={14} />
								Save as a prompt
							</InputGroupButton>
						</AIPromptBoxToolbarEnd>
					) : null}
				</AIPromptBoxToolbar>
				<AIPromptBoxTextarea
					aria-describedby={problem ? problemId : undefined}
					aria-invalid={!!problem || !!invalid}
					aria-labelledby={ariaLabelledBy}
					disabled={disabled}
					id={id}
					maxLength={maxLength}
					onBlur={onBlur}
					onChange={event => field.onChange(event.target.value)}
					onFocus={onFocus}
					placeholder={placeholder}
					ref={field.ref}
					rows={rows}
					styles={textAreaStyles}
					value={field.value}
				/>
				{footer}
			</AIPromptBox>
			{problem ? <FieldError id={problemId}>{problem}</FieldError> : null}
			{field.sourcePrompt ? (
				<AIPromptBoxCredit>
					<Pill color='gray' label={`Started from ${field.sourcePrompt.name}`} variant='tag' />
					<span>{copy.attributionNote}</span>
				</AIPromptBoxCredit>
			) : null}

			<SavePromptDialog
				description={copy.saveDescription}
				impersonationContext={impersonationContext}
				isOpen={isSavingPrompt}
				namePlaceholder={copy.saveNamePlaceholder}
				note={copy.saveNote}
				onOpenChange={setIsSavingPrompt}
				onSaved={field.onSaved}
				text={field.value.trim()}
				usage={usage}
			/>
			<AIPromptPanel
				description={hasValue ? copy.panelReplaceDescription : copy.panelDescription}
				impersonationContext={impersonationContext}
				isOpen={isPanelOpen}
				onOpenChange={setIsPanelOpen}
				onPromptSelected={field.applyPrompt}
				savedEmptyText={copy.savedEmptyText}
				showSamples={showSamples}
				usage={usage}
			/>
		</>
	);
};
